import React from 'react';
import PropTypes, { func } from 'prop-types';
import { Description } from '@material-ui/icons';
import LinearProgress from '@material-ui/core/LinearProgress';
import * as moment from 'moment';

import CardActions from '@material-ui/core/CardActions';
import { DropzoneArea } from 'material-ui-dropzone';

import { Button, Checkbox } from 'school-space-client/components';
import { dateFromDatetime } from 'school-space-utils/date';
import useStyles from 'dashboard/screens/SupportingInfoStyle';

const css = {
    fileInput: {
        width: '50%'
    },
    checkbox: {
        marginRight: 10
    }
};
const UploadingSupportingInfo = ({
    document,
    setDocument,
    tempDocument,
    setTempDocument,
    expiryDate,
    setExpiryDate,
    updateExpiryDate,
    updatedDate,
    progress,
    submit,
    title,
    showExpiryDate,
    showExempt,
    isExempt,
    exemptFlag,
    setIsExempted,
    documentUrl,
    onRemove
}) => {
    const titleText =
        document.length === 0
            ? `Please upload your ${title}`
            : `Thank you for uploading your ${title} document`;
    const subText =
        document.length === 0
            ? 'The document upload should be in the form .pdf, .doc, .docx. Maximum file size of 50Mb.'
            : 'You’ll receive a notification when your document is close to expiring.';
    return (
        <>
            <h5 className="main-title">
                {exemptFlag ? `Thanks for letting us know you’re exempt` : titleText}
            </h5>
            <p className="subTitle">
                {exemptFlag
                    ? `As none of your attendees are under 18s or vulnerable adults, a safeguarding policy is not required. `
                    : subText}
            </p>
            {document.length > 0 && (
                <ReUploadSupportingDocuments
                    showExpiryDate={showExpiryDate}
                    name={document}
                    expiryDate={expiryDate}
                    submit={submit}
                    tempDocument={tempDocument}
                    setTempDocument={setTempDocument}
                    updateExpiryDate={updateExpiryDate}
                    updatedDate={updatedDate}
                    isExempt={isExempt}
                    exemptFlag={exemptFlag}
                    setIsExempted={setIsExempted}
                    showExempt={showExempt}
                    documentUrl={documentUrl}
                    onRemove={onRemove}
                />
            )}
            {document.length === 0 && (
                <UploadingSupportingDocuments
                    document={document}
                    setDocument={setDocument}
                    progress={progress}
                    showExpiryDate={showExpiryDate}
                    showExempt={showExempt}
                    isExempt={isExempt}
                    exemptFlag={exemptFlag}
                    setIsExempted={setIsExempted}
                    expiryDate={expiryDate}
                    tempDocument={tempDocument}
                    setTempDocument={setTempDocument}
                    setExpiryDate={setExpiryDate}
                    submit={submit}
                    onRemove={onRemove}
                />
            )}
        </>
    );
};

UploadingSupportingInfo.defaultProps = {
    document: '',
    expiryDate: '',
    progress: 0,
    updatedDate: '',
    tempDocument: [],
    showExpiryDate: false,
    showExempt: false,
    isExempt: false,
    exemptFlag: false,
    setIsExempted: func,
    documentUrl: '',
    onRemove: null
};

const strucTempDocument = PropTypes.arrayOf({
    lastModified: PropTypes.number,
    lastModifiedDate: PropTypes.string,
    name: PropTypes.string,
    path: PropTypes.string,
    size: PropTypes.number,
    type: PropTypes.string,
    webkitRelativePath: PropTypes.string
});

UploadingSupportingInfo.propTypes = {
    document: PropTypes.string,
    setIsExempted: PropTypes.func,
    setDocument: PropTypes.func.isRequired,
    updateExpiryDate: PropTypes.func.isRequired,
    showExpiryDate: PropTypes.bool,
    showExempt: PropTypes.bool,
    expiryDate: PropTypes.string,
    updatedDate: PropTypes.string,
    isExempt: PropTypes.bool,
    exemptFlag: PropTypes.bool,
    setExpiryDate: PropTypes.func.isRequired,

    tempDocument: PropTypes.shape(strucTempDocument),
    setTempDocument: PropTypes.func.isRequired,

    progress: PropTypes.number,
    submit: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    documentUrl: PropTypes.string,
    onRemove: PropTypes.func
};

export default UploadingSupportingInfo;

const UploadingSupportingDocuments = ({
    progress,
    expiryDate,
    showExpiryDate,
    showExempt,
    isExempt,
    setIsExempted,
    tempDocument,
    setTempDocument,
    setExpiryDate,
    submit,
    onRemove
}) => {
    const classes = useStyles();
    if (progress === 0) {
        return (
            <form>
                {!isExempt && (
                    <div className="dropboxMain">
                        {tempDocument.length === 0 ? (
                            <DropzoneArea
                                acceptedFiles={[
                                    'application/pdf',
                                    'application/msword',
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                ]}
                                showFileNames
                                Icon={Description}
                                dropzoneText="Drag and drop your file"
                                filesLimit={1}
                                dropzoneClass={classes.dropZone}
                                onChange={value => {
                                    setTempDocument(value);
                                }}
                            />
                        ) : (
                            <div className="d-flex justify-content-between filePathbox">
                                <div className="d-flex justify-content-start align-items-center">
                                    <div>
                                        <svg
                                            width="48"
                                            height="48"
                                            viewBox="0 0 48 48"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M42.2812 12.6562L32.4375 9.84375L29.625 0H9.9375C7.60753 0 5.71875 1.88878 5.71875 4.21875V43.7812C5.71875 46.1112 7.60753 48 9.9375 48H38.0625C40.3925 48 42.2812 46.1112 42.2812 43.7812V12.6562Z"
                                                fill="#3E9BD9"
                                            />
                                            <path
                                                d="M42.2812 12.6562V43.7812C42.2812 46.1112 40.3925 48 38.0625 48H24V0H29.625L32.4375 9.84375L42.2812 12.6562Z"
                                                fill="#3589C0"
                                            />
                                            <path
                                                d="M42.2812 12.6562H32.4375C30.8906 12.6562 29.625 11.3906 29.625 9.84375V0C29.9906 0 30.3562 0.140625 30.6093 0.421969L41.8593 11.672C42.1406 11.925 42.2812 12.2906 42.2812 12.6562Z"
                                                fill="#68B1E1"
                                            />
                                            <path
                                                d="M32.4375 22.5938H15.5625C14.7852 22.5938 14.1562 21.9648 14.1562 21.1875C14.1562 20.4102 14.7852 19.7812 15.5625 19.7812H32.4375C33.2148 19.7812 33.8438 20.4102 33.8438 21.1875C33.8438 21.9648 33.2148 22.5938 32.4375 22.5938Z"
                                                fill="#FFF5F5"
                                            />
                                            <path
                                                d="M32.4375 28.2188H15.5625C14.7852 28.2188 14.1562 27.5898 14.1562 26.8125C14.1562 26.0352 14.7852 25.4062 15.5625 25.4062H32.4375C33.2148 25.4062 33.8438 26.0352 33.8438 26.8125C33.8438 27.5898 33.2148 28.2188 32.4375 28.2188Z"
                                                fill="#FFF5F5"
                                            />
                                            <path
                                                d="M32.4375 33.8438H15.5625C14.7852 33.8438 14.1562 33.2148 14.1562 32.4375C14.1562 31.6602 14.7852 31.0312 15.5625 31.0312H32.4375C33.2148 31.0312 33.8438 31.6602 33.8438 32.4375C33.8438 33.2148 33.2148 33.8438 32.4375 33.8438Z"
                                                fill="#FFF5F5"
                                            />
                                            <path
                                                d="M26.8125 39.4688H15.5625C14.7852 39.4688 14.1562 38.8398 14.1562 38.0625C14.1562 37.2852 14.7852 36.6562 15.5625 36.6562H26.8125C27.5898 36.6562 28.2188 37.2852 28.2188 38.0625C28.2188 38.8398 27.5898 39.4688 26.8125 39.4688Z"
                                                fill="#FFF5F5"
                                            />
                                            <path
                                                d="M24 39.4688H26.8125C27.5898 39.4688 28.2188 38.8398 28.2188 38.0625C28.2188 37.2852 27.5898 36.6562 26.8125 36.6562H24V39.4688Z"
                                                fill="#E3E7EA"
                                            />
                                            <path
                                                d="M24 33.8438H32.4375C33.2148 33.8438 33.8438 33.2148 33.8438 32.4375C33.8438 31.6602 33.2148 31.0312 32.4375 31.0312H24V33.8438Z"
                                                fill="#E3E7EA"
                                            />
                                            <path
                                                d="M24 28.2188H32.4375C33.2148 28.2188 33.8438 27.5898 33.8438 26.8125C33.8438 26.0352 33.2148 25.4062 32.4375 25.4062H24V28.2188Z"
                                                fill="#E3E7EA"
                                            />
                                            <path
                                                d="M24 22.5938H32.4375C33.2148 22.5938 33.8438 21.9648 33.8438 21.1875C33.8438 20.4102 33.2148 19.7812 32.4375 19.7812H24V22.5938Z"
                                                fill="#E3E7EA"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="filename">{tempDocument[0].name}</p>
                                    </div>
                                </div>
                                <div
                                    onClick={() => setTempDocument([])}
                                    role="presentation"
                                    type="button"
                                >
                                    <img src="images/close.png" alt="close" />
                                </div>
                            </div>
                        )}
                        <div className="form-group expiry-date-container">
                            {showExpiryDate && (
                                <input
                                    type="date"
                                    className="form-control"
                                    name="Expiry"
                                    min={moment().format('YYYY-MM-DD')}
                                    value={
                                        expiryDate === '' ? moment() : dateFromDatetime(expiryDate)
                                    }
                                    onChange={event =>
                                        setExpiryDate(dateFromDatetime(event.target.value))
                                    }
                                    required
                                    placeholder="Expiry date"
                                />
                            )}
                        </div>
                    </div>
                )}

                {showExempt && (
                    <div className="form-group">
                        <Checkbox
                            name="exemptSafegaurdPolicy"
                            label=" I confirm that none of my attendees are under 18s or vulnerable adults"
                            inlineLabel
                            checked={isExempt}
                            onChange={value => setIsExempted(value)}
                        />
                    </div>
                )}
                {isExempt && onRemove && (
                    <CardActions className={classes.actionConatiner}>
                        <button className="btn btnnext" type="button" onClick={onRemove}>
                            Remove exemption
                        </button>
                    </CardActions>
                )}
                <div className={classes.line} />
                <CardActions className={classes.actionConatiner}>
                    <Button size="medium" onClick={submit}>
                        Save
                    </Button>
                </CardActions>
            </form>
        );
    }
    return (
        <div className={classes.progressBar}>
            <div className={classes.progressBarContent}>
                <div className="flexCss">
                    <div className={classes.progressBarHeading}>Uploading</div>
                    {/* <div className="iconCss">
                        <Close size="10" />
                    </div> */}
                </div>
                <div className={classes.progressBarSubHeading}>uploading...</div>
            </div>
            <LinearProgress />
        </div>
    );
};

UploadingSupportingDocuments.defaultProps = {
    expiryDate: '',
    showExpiryDate: false,
    showExempt: false,
    isExempt: false,
    setIsExempted: func,
    progress: 0,
    tempDocument: [],
    onRemove: null
};

UploadingSupportingDocuments.propTypes = {
    tempDocument: PropTypes.shape(strucTempDocument),
    setTempDocument: PropTypes.func.isRequired,
    showExempt: PropTypes.bool,
    isExempt: PropTypes.bool,
    setIsExempted: PropTypes.func,
    showExpiryDate: PropTypes.bool,
    expiryDate: PropTypes.string,
    onRemove: PropTypes.func,
    setExpiryDate: PropTypes.func.isRequired,
    progress: PropTypes.number,
    submit: PropTypes.func.isRequired
};

const ReUploadSupportingDocuments = ({
    name,
    expiryDate,
    submit,
    tempDocument,
    setTempDocument,
    showExpiryDate,
    showExempt,
    isExempt,
    setIsExempted,
    updateExpiryDate,
    updatedDate,
    documentUrl,
    onRemove
}) => {
    const classes = useStyles();
    const hiddenFileInput = React.useRef(null);
    const handleClick = () => {
        hiddenFileInput.current.click();
    };
    const idleClick = ev => {
        ev.preventDefault();
    };
    return (
        <form>
            <div className="d-flex justify-content-between filePathbox">
                <div
                    className="d-flex justify-content-start align-items-center"
                    onClick={
                        moment() > moment(expiryDate).subtract(7, 'days') ? handleClick : idleClick
                    }
                    aria-hidden="true"
                >
                    {moment() > moment(expiryDate).subtract(7, 'days') ? (
                        <input
                            type="file"
                            ref={hiddenFileInput}
                            onChange={event => {
                                const fileUploaded = event.target.files;
                                setTempDocument(fileUploaded);
                            }}
                            style={{ display: 'none' }}
                        />
                    ) : null}

                    <div>
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M42.2812 12.6562L32.4375 9.84375L29.625 0H9.9375C7.60753 0 5.71875 1.88878 5.71875 4.21875V43.7812C5.71875 46.1112 7.60753 48 9.9375 48H38.0625C40.3925 48 42.2812 46.1112 42.2812 43.7812V12.6562Z"
                                fill="#3E9BD9"
                            />
                            <path
                                d="M42.2812 12.6562V43.7812C42.2812 46.1112 40.3925 48 38.0625 48H24V0H29.625L32.4375 9.84375L42.2812 12.6562Z"
                                fill="#3589C0"
                            />
                            <path
                                d="M42.2812 12.6562H32.4375C30.8906 12.6562 29.625 11.3906 29.625 9.84375V0C29.9906 0 30.3562 0.140625 30.6093 0.421969L41.8593 11.672C42.1406 11.925 42.2812 12.2906 42.2812 12.6562Z"
                                fill="#68B1E1"
                            />
                            <path
                                d="M32.4375 22.5938H15.5625C14.7852 22.5938 14.1562 21.9648 14.1562 21.1875C14.1562 20.4102 14.7852 19.7812 15.5625 19.7812H32.4375C33.2148 19.7812 33.8438 20.4102 33.8438 21.1875C33.8438 21.9648 33.2148 22.5938 32.4375 22.5938Z"
                                fill="#FFF5F5"
                            />
                            <path
                                d="M32.4375 28.2188H15.5625C14.7852 28.2188 14.1562 27.5898 14.1562 26.8125C14.1562 26.0352 14.7852 25.4062 15.5625 25.4062H32.4375C33.2148 25.4062 33.8438 26.0352 33.8438 26.8125C33.8438 27.5898 33.2148 28.2188 32.4375 28.2188Z"
                                fill="#FFF5F5"
                            />
                            <path
                                d="M32.4375 33.8438H15.5625C14.7852 33.8438 14.1562 33.2148 14.1562 32.4375C14.1562 31.6602 14.7852 31.0312 15.5625 31.0312H32.4375C33.2148 31.0312 33.8438 31.6602 33.8438 32.4375C33.8438 33.2148 33.2148 33.8438 32.4375 33.8438Z"
                                fill="#FFF5F5"
                            />
                            <path
                                d="M26.8125 39.4688H15.5625C14.7852 39.4688 14.1562 38.8398 14.1562 38.0625C14.1562 37.2852 14.7852 36.6562 15.5625 36.6562H26.8125C27.5898 36.6562 28.2188 37.2852 28.2188 38.0625C28.2188 38.8398 27.5898 39.4688 26.8125 39.4688Z"
                                fill="#FFF5F5"
                            />
                            <path
                                d="M24 39.4688H26.8125C27.5898 39.4688 28.2188 38.8398 28.2188 38.0625C28.2188 37.2852 27.5898 36.6562 26.8125 36.6562H24V39.4688Z"
                                fill="#E3E7EA"
                            />
                            <path
                                d="M24 33.8438H32.4375C33.2148 33.8438 33.8438 33.2148 33.8438 32.4375C33.8438 31.6602 33.2148 31.0312 32.4375 31.0312H24V33.8438Z"
                                fill="#E3E7EA"
                            />
                            <path
                                d="M24 28.2188H32.4375C33.2148 28.2188 33.8438 27.5898 33.8438 26.8125C33.8438 26.0352 33.2148 25.4062 32.4375 25.4062H24V28.2188Z"
                                fill="#E3E7EA"
                            />
                            <path
                                d="M24 22.5938H32.4375C33.2148 22.5938 33.8438 21.9648 33.8438 21.1875C33.8438 20.4102 33.2148 19.7812 32.4375 19.7812H24V22.5938Z"
                                fill="#E3E7EA"
                            />
                        </svg>
                    </div>
                    <div>
                        <p className="filename">
                            {tempDocument.length > 0 ? tempDocument[0].name : name}
                        </p>
                        {showExpiryDate && (
                            <p
                                className={
                                    moment() > moment(expiryDate).subtract(7, 'days')
                                        ? 'alert-msg'
                                        : 'exp'
                                }
                            >
                                Expiry date:
                                {moment(expiryDate).format('DD/MM/YYYY')}
                            </p>
                        )}
                    </div>
                </div>
            </div>
            {(documentUrl || onRemove) && (
                <CardActions className={classes.flexStart}>
                    {documentUrl && !isExempt && (
                        <a
                            className="btn btnnext"
                            href={documentUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View
                        </a>
                    )}
                    {onRemove && (
                        <button className="btn btnnext" type="button" onClick={onRemove}>
                            {isExempt ? 'Remove exemption' : 'Remove'}
                        </button>
                    )}
                </CardActions>
            )}
            {showExpiryDate && moment() > moment(expiryDate).subtract(7, 'days') && (
                <p className="alert-msg">
                    Your document is going to expire soon, please upload an updated version.
                </p>
            )}
            {/* <div className="form-group expiry-date-container"> */}
            {showExpiryDate &&
                moment() > moment(expiryDate).subtract(7, 'days') &&
                tempDocument.length > 0 && (
                    <input
                        type="date"
                        style={css.fileInput}
                        className="form-control"
                        name="Expiry"
                        min={moment().format('YYYY-MM-DD')}
                        value={updatedDate === '' ? moment() : dateFromDatetime(updatedDate)}
                        onChange={event => updateExpiryDate(dateFromDatetime(event.target.value))}
                        required
                        placeholder="Expiry date"
                    />
                )}
            {showExempt && (
                <div className="form-group">
                    <Checkbox
                        name="exemptSafegaurdPolicy"
                        label=" I confirm that none of my attendees are under 18s or vulnerable adults"
                        inlineLabel
                        checked={isExempt}
                        onChange={value => setIsExempted(value)}
                    />
                </div>
            )}
            {((showExpiryDate && moment() > moment(expiryDate).subtract(7, 'days')) ||
                isExempt) && (
                <CardActions className={classes.flexStart}>
                    <button className="btn btnnext" type="button" onClick={submit}>
                        Save
                    </button>
                </CardActions>
            )}
        </form>
    );
};

ReUploadSupportingDocuments.defaultProps = {
    name: '',
    expiryDate: '',
    updatedDate: '',
    showExpiryDate: false,
    showExempt: false,
    isExempt: false,
    tempDocument: [],
    setIsExempted: func,
    documentUrl: '',
    onRemove: null
};

ReUploadSupportingDocuments.propTypes = {
    name: PropTypes.string,
    showExpiryDate: PropTypes.bool,
    showExempt: PropTypes.bool,
    isExempt: PropTypes.bool,
    updateExpiryDate: PropTypes.func.isRequired,
    updatedDate: PropTypes.string,
    expiryDate: PropTypes.string,
    setIsExempted: PropTypes.func,
    submit: PropTypes.func.isRequired,
    tempDocument: PropTypes.shape(strucTempDocument),
    setTempDocument: PropTypes.func.isRequired,
    documentUrl: PropTypes.string,
    onRemove: PropTypes.func
};
